import React from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { ClickableIcon, Link } from '~components';
import { faArrowUpRightFromSquare, faRepeat } from '~components/icon';
import { Row } from '~components/table-view';

import { getTP1ClientDetailsUrl } from '~constants/routes-tp1';

import { useCopyDexDataMutation } from '~dex/actions/dex';

import DexCopyPreviewPopover from './DexCopyPreviewPopover';

interface ComponentProps {
  row?: Row;
}

const DexReadinessActionCell: React.FC<ComponentProps> = ({ row }) => {
  const { showToast } = useToast();
  const [copyDexData, { isLoading: isCopying }] = useCopyDexDataMutation();

  // TAV-2962: a mac_id-only row flags a client-level gap (My Aged Care number), editable only
  // on the client's DEX/HACC Settings screen in TP1, not on this appointment - the tooltip below
  // says so. The icon itself still opens the appointment (Beth Kairys review feedback, TAV-3133:
  // disabling it left no way to verify a just-completed copy or check the row's other gaps).
  const missingFields: string[] = row?.missing_fields ?? [];
  const isMacIdOnly = missingFields.length === 1 && missingFields[0] === 'mac_id';

  const handleOpenOccurrence = () => {
    if (row?.appointment_id) {
      window.open(`/appointments/${row.appointment_id}?tab=dex`, '_blank', 'noopener,noreferrer');
    }
  };

  // TAV-3133: sits alongside the "open occurrence" action above, only on rows that have an
  // originating repeat appointment - so staff can check/fill in the source data before copying.
  const handleOpenRepeat = () => {
    if (row?.parent_appointment_id) {
      window.open(`/appointments/${row.parent_appointment_id}?tab=dex`, '_blank', 'noopener,noreferrer');
    }
  };

  // TAV-3133: eligibility is always re-validated server-side - dex_copy_eligible only decides
  // whether this action renders at all, never whether the write is actually authorised, so a
  // race (e.g. someone else copies onto it first) surfaces as an empty `copied` array here.
  const handleCopy = async () => {
    try {
      const result = await copyDexData({ appointment_id: row?.appointment_id }).unwrap();
      if (result.copied.length > 0) {
        showToast('success', `Copied DEX data from appointment ${row?.parent_reference_number ?? row?.parent_appointment_id}`);
      } else {
        showToast('info', 'This appointment is no longer eligible to copy DEX data - it may have changed elsewhere');
      }
    } catch (error) {
      showToast('danger', getErrorMessage(error));
      // Swallowed rather than rethrown - DexCopyPreviewPopover always closes after calling this,
      // regardless of outcome, since the toast above is what actually reports success/failure.
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center gap-2 w-100">
      <ClickableIcon
        id={`dex-readiness-action-${row?.appointment_id ?? 'unknown'}`}
        icon={faArrowUpRightFromSquare}
        onClick={handleOpenOccurrence}
        toolTip={
          isMacIdOnly ? (
            <>
              My Aged Care ID is maintained on the client&apos;s DEX/HACC Settings screen in TP1, not on this
              appointment.{' '}
              {row?.client_id != null && (
                // TooltipPortal renders via createPortal into document.body, but React still
                // bubbles this click through the *React* tree (not the DOM tree) up to the
                // icon's own onClick below - stopPropagation keeps this link from also opening
                // the appointment.
                <Link
                  to={getTP1ClientDetailsUrl(row.client_id)}
                  target="_blank"
                  onClick={(event) => event.stopPropagation()}
                >
                  Update in TP1
                </Link>
              )}
            </>
          ) : (
            'Open appointment'
          )
        }
        tooltipPersistent={isMacIdOnly}
        tooltipLimitWidth={isMacIdOnly ? 260 : undefined}
        ariaLabel={isMacIdOnly ? 'My Aged Care ID editable in TP1 DEX/HACC Settings' : 'Open appointment'}
        dataCy={`dex-readiness-action-${row?.appointment_id ?? 'unknown'}`}
      />
      {/* Boolean(...), not a bare truthy check: a data quirk on some rows returns
          parent_appointment_id: 0 rather than null for "no parent" - `0 && x` evaluates to `0`,
          which React renders as a literal stray "0" next to the icon (canary functional-test
          finding, TAV-3133). */}
      {Boolean(row?.parent_appointment_id) && (
        <ClickableIcon
          id={`dex-readiness-open-repeat-${row?.appointment_id ?? 'unknown'}`}
          icon={faRepeat}
          onClick={handleOpenRepeat}
          toolTip="Open originating repeat appointment"
          ariaLabel="Open originating repeat appointment"
          dataCy={`dex-readiness-open-repeat-${row?.appointment_id ?? 'unknown'}`}
        />
      )}
      {row?.dex_copy_eligible && Boolean(row?.parent_appointment_id) && row?.copy_preview && (
        <DexCopyPreviewPopover
          id={String(row.appointment_id ?? 'unknown')}
          parentReference={row.parent_reference_number ?? row.parent_appointment_id}
          preview={row.copy_preview}
          onConfirm={handleCopy}
          isLoading={isCopying}
        />
      )}
    </div>
  );
};

export default DexReadinessActionCell;
